import { defineStore } from 'pinia';
import { message } from 'ant-design-vue';
import { ref, computed } from 'vue';
import type { ComputedRef } from 'vue';

import { dayJsToString, warningIfNotUtcMidnight, dateStrToDayJs } from '@/components/common/dateUtils';
import i18n from '@/i18n';
import Consts from '@/api/Consts';

import type { ChartDatesRange, GnssTooltipOption, SessionConf, SessionConfPatch } from '@/stores/types/sessionTypes';
import {
	isBoolean,
	isNullableNumber,
	isNullableString,
	isNumber,
	isRecord,
	isString,
	isStringArray,
} from '@/utils/typeGuards';
import { readVersionedLocalStorage, writeVersionedLocalStorage } from '@/utils/versionedLocalStorage';


const SESSION = 'session';
const VERSION = 9;

const DEFAULT_GNSS_TOOLTIP_ITEMS = [
	'satellites',
	'signals',
	'rtcm-messages',
] as const satisfies readonly GnssTooltipOption[];


const isSessionConf = (value: unknown): value is SessionConf => {
	if (!isRecord(value)) {
		return false;
	}
	return (
		isNumber(value.version) &&
		isString(value.closeButtonRoute) &&
		isNullableString(value.chartDatesRangeStart) &&
		isNullableString(value.chartDatesRangeEnd) &&
		isNullableNumber(value.chartDatesRangeLastAccess) &&
		isString(value.locale) &&
		isBoolean(value.debug) &&
		isBoolean(value.mapModeChooserCollapsed) &&
		isNumber(value.dashboardUpdatePeriodMinutes) &&
		isNumber(value.userPulseStatsPeriod) &&
		isStringArray(value.mapModeGnssBSTooltipItems) &&
		isString(value.projectTitle)
	);
};


const defaultSessionConf = (): SessionConf => ({
	closeButtonRoute: '',
	chartDatesRangeStart: null,
	chartDatesRangeEnd: null,
	chartDatesRangeLastAccess: null,
	locale: 'en',
	debug: false,
	mapModeChooserCollapsed: false,
	dashboardUpdatePeriodMinutes: 5,
	userPulseStatsPeriod: 1,
	mapModeGnssBSTooltipItems: [...DEFAULT_GNSS_TOOLTIP_ITEMS],
	projectTitle: Consts.PROJECT_TITLE_DEFAULT,
	version: VERSION,
});


const readSessionConf = (): SessionConf => {
	const data = readVersionedLocalStorage(SESSION, VERSION, isSessionConf, defaultSessionConf());
	return {
		...data,
		projectTitle: data.projectTitle || Consts.PROJECT_TITLE_DEFAULT,
	};
};


const writeSessionConf = (data: SessionConfPatch): void => {
	writeVersionedLocalStorage(SESSION, VERSION, readSessionConf, data);
};


export const useSessionStore = defineStore('session', () => {
	const conf = readSessionConf();
	const closeButtonRoute = ref(conf.closeButtonRoute);
	const chartDatesRangeStart = ref<string | null>(conf.chartDatesRangeStart);
	const chartDatesRangeEnd = ref<string | null>(conf.chartDatesRangeEnd);
	const chartDatesRangeLastAccess = ref<number | null>(conf.chartDatesRangeLastAccess);
	const locale = ref(conf.locale);
	const debug = ref(conf.debug);
	const mapModeChooserCollapsed = ref(conf.mapModeChooserCollapsed);
	const dashboardUpdatePeriodMinutes = ref(conf.dashboardUpdatePeriodMinutes);
	const userPulseStatsPeriod = ref(conf.userPulseStatsPeriod);
	const chartDatesRangeUpdated = ref(false);
	const mapModeGnssBSTooltipItems = ref<string[]>(conf.mapModeGnssBSTooltipItems);
	const projectTitle = ref(conf.projectTitle);

	i18n.global.locale.value = locale.value;

	const updateCloseButtonRoute = (routeName: string): void => {
		closeButtonRoute.value = routeName;
		writeSessionConf({ closeButtonRoute: closeButtonRoute.value });
	};

	const updateChartDatesRange = (value: ChartDatesRange | null): void => {
		if (value == null || value.length < 2) {
			chartDatesRangeStart.value = null;
			chartDatesRangeEnd.value = null;
		} else {
			const [start, end] = value;
			warningIfNotUtcMidnight(start, 'start', 'updateChartDatesRange');
			warningIfNotUtcMidnight(end, 'end', 'updateChartDatesRange');
			chartDatesRangeStart.value = dayJsToString(start);
			chartDatesRangeEnd.value = dayJsToString(end);
		}
		chartDatesRangeUpdated.value = true;
		writeSessionConf({
			chartDatesRangeStart: chartDatesRangeStart.value,
			chartDatesRangeEnd: chartDatesRangeEnd.value,
		});
		triggerChartDatesRangeAccess();
	};

	const triggerChartDatesRangeAccess = (): void => {
		chartDatesRangeLastAccess.value = (new Date()).getTime();
		writeSessionConf({
			chartDatesRangeLastAccess: chartDatesRangeLastAccess.value,
		});
	};

	const checkChartDatesRangeAccess = (): void => {
		if (chartDatesRangeLastAccess.value != null) {
			const totalMilliSeconds = (new Date()).getTime() - chartDatesRangeLastAccess.value;
			if (totalMilliSeconds > 2 * 3600 * 1000) {
				updateChartDatesRange(null);
				chartDatesRangeUpdated.value = false;
			}
		}
		triggerChartDatesRangeAccess();
	};

	const updateLocale = (value: string): void => {
		locale.value = value;
		i18n.global.locale.value = locale.value;
		writeSessionConf({ locale: locale.value });
	};

	const toggleDebugMode = (): void => {
		debug.value = !debug.value;
		if (debug.value) {
			message.warning('Debug mode ON');
		} else {
			message.info('Debug mode OFF');
		}
		writeSessionConf({ debug: debug.value });
	};

	const toggleMapModeChooserCollapsed = (): void => {
		mapModeChooserCollapsed.value = !mapModeChooserCollapsed.value;
		writeSessionConf({ mapModeChooserCollapsed: mapModeChooserCollapsed.value });
	};

	const chartDatesRange: ComputedRef<ChartDatesRange | null> = computed(() => {
		if (chartDatesRangeStart.value != null && chartDatesRangeEnd.value != null) {
			return [dateStrToDayJs(chartDatesRangeStart.value), dateStrToDayJs(chartDatesRangeEnd.value)];
		}
		return null;
	});

	const updateDashboardUpdatePeriodMinutes = (value: number): void => {
		dashboardUpdatePeriodMinutes.value = value;
		writeSessionConf({ dashboardUpdatePeriodMinutes: dashboardUpdatePeriodMinutes.value });
	};

	const updateUserPulseStatsPeriod = (value: number): void => {
		userPulseStatsPeriod.value = value;
		writeSessionConf({ userPulseStatsPeriod: userPulseStatsPeriod.value });
	};

	const updateMapModeGnssBSTooltipItems = (value: string[]): void => {
		mapModeGnssBSTooltipItems.value = value;
		writeSessionConf({ mapModeGnssBSTooltipItems: mapModeGnssBSTooltipItems.value });
	};

	const updateProjectTitle = (value: string): void => {
		projectTitle.value = value || Consts.PROJECT_TITLE_DEFAULT;
		writeSessionConf({ projectTitle: projectTitle.value });
	};

	return {
		closeButtonRoute,
		chartDatesRangeStart,
		chartDatesRangeEnd,
		chartDatesRangeLastAccess,
		locale,
		debug,
		mapModeChooserCollapsed,
		dashboardUpdatePeriodMinutes,
		userPulseStatsPeriod,
		chartDatesRangeUpdated,
		mapModeGnssBSTooltipItems,
		projectTitle,
		updateCloseButtonRoute,
		updateChartDatesRange,
		triggerChartDatesRangeAccess,
		checkChartDatesRangeAccess,
		updateLocale,
		toggleDebugMode,
		toggleMapModeChooserCollapsed,
		chartDatesRange,
		updateDashboardUpdatePeriodMinutes,
		updateUserPulseStatsPeriod,
		updateMapModeGnssBSTooltipItems,
		updateProjectTitle,
	};
});
